import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Compass, Mountain } from "lucide-react";
import { PeakMark } from "@/components/PeakMark";

const tags = [
  "Founders building what's next",
  "Executives stepping into bigger rooms",
  "Nonprofit & mission-driven leaders",
  "Operators in a season of pressure",
  "Brands navigating reinvention",
  "Teams ready to scale with discipline",
];

const bg =
  "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJhbCUyMGRldGFpbHN8ZW58MHx8fHwxNzgyNzg5NDU0fDA&ixlib=rb-4.1.0&q=85";

export const IdealClient = () => {
  return (
    <section
      id="ideal-client"
      data-testid="ideal-client-section"
      className="relative py-24 md:py-32 bg-[#0A0A0A] text-white overflow-hidden ssg-grain"
    >
      <div
        className="absolute inset-0 opacity-[0.12] mix-blend-luminosity pointer-events-none"
        style={{
          backgroundImage: `url(${bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/90 to-[#0A0A0A] pointer-events-none" />

      {/* Ambient red glow — matches Performance section */}
      <div
        className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #9B1C1C 0%, transparent 65%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-16 h-px bg-[#E25555]" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]">
              Ideal Client
            </span>
          </div>
          <h2
            data-testid="ideal-client-heading"
            className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.0]"
          >
            For the<br />
            <span className="italic font-light">capable, driven,</span><br />
            and unfinished.
          </h2>

          {/* Visual anchor — trio matching the three descriptors */}
          <div
            data-testid="ideal-client-trio"
            className="mt-12 relative"
          >
            <div className="grid grid-cols-3 gap-4 max-w-md">
              {[
                { icon: ShieldCheck, label: "Capable" },
                { icon: Compass, label: "Driven" },
                { icon: Mountain, label: "Unfinished" },
              ].map(({ icon: Icon, label }, i) => (
                <div
                  key={label}
                  data-testid={`ideal-client-trio-${i}`}
                  className="flex flex-col items-center text-center group"
                >
                  <div className="relative flex items-center justify-center w-16 h-16 mb-4">
                    <span className="absolute inset-0 rounded-full border border-[#9B1C1C]/50 group-hover:border-[#E25555] transition-colors" />
                    <span className="absolute inset-2 rounded-full bg-[#9B1C1C]/10 group-hover:bg-[#9B1C1C]/25 transition-colors" />
                    <Icon size={24} strokeWidth={1.5} className="text-white relative" />
                  </div>
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-neutral-300">
                    {label}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex items-center gap-4 max-w-md">
              <PeakMark size={18} className="text-white/50" />
              <span className="text-[10px] tracking-[0.28em] uppercase font-bold text-neutral-500">
                Built for the becoming
              </span>
              <span className="flex-1 h-px bg-white/10" />
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7 lg:pt-6"
        >
          <p className="text-lg md:text-xl leading-relaxed text-neutral-200 font-medium">
            SSG is built for leaders, founders, professionals, and
            organizations in a season of <span className="text-white font-semibold">growth, transition, pressure, or reinvention</span>.
            They&apos;re capable, driven, and ambitious — but they need
            structure, perspective, strategy, and execution support to take
            the next real step.
          </p>

          <p className="mt-6 text-base leading-relaxed text-neutral-400 font-medium">
            If you&apos;re carrying more than you can name, building something
            that matters, or stepping into a version of yourself you
            haven&apos;t fully met yet — this work is for you.
          </p>

          <div className="mt-12 flex flex-wrap gap-3">
            {tags.map((t, i) => (
              <span
                key={t}
                data-testid={`ideal-tag-${i}`}
                className="inline-flex items-center px-4 py-2 text-[12px] tracking-wide font-medium text-neutral-200 border border-white/15 hover:border-[#E25555] hover:text-white transition-colors"
              >
                {t}
              </span>
            ))}
          </div>

          <div className="mt-14 pt-10 border-t border-white/10 grid sm:grid-cols-3 gap-8">
            {[
              { k: "Clarity", v: "Name what matters most." },
              { k: "Strategy", v: "Build the plan that carries it." },
              { k: "Execution", v: "Move with discipline and confidence." },
            ].map((c, i) => (
              <div key={c.k} data-testid={`ideal-pillar-${i}`}>
                <div className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]">
                  /0{i + 1}
                </div>
                <div className="mt-3 font-display text-xl font-bold tracking-tight">
                  {c.k}
                </div>
                <div className="mt-2 text-sm text-neutral-400 font-medium">
                  {c.v}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default IdealClient;
